// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { 
  Calculator, 
  FileText, 
  BarChart3, 
  Target, 
  Users, 
  MessageSquare,
  Mail,
  Calendar,
  DollarSign,
  Zap,
  Smartphone,
  Globe
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import ROICalculator from "@/components/sales/tools/ROICalculator";
import ProposalTemplates from "@/components/sales/tools/ProposalTemplates";
import CPMCalculator from "@/components/sales/tools/CPMCalculator";
import ConversionCalculator from "@/components/sales/tools/ConversionCalculator";

const SalesToolsPage: React.FC = () => {
  const { profile } = useAuth();
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalProspects: 0,
    toolsUsed: 0,
    successRate: 0
  });
  const [loading, setLoading] = useState(true);
  const [activeModal, setActiveModal] = useState<string | null>(null);

  useEffect(() => {
    if (profile?.id) {
      fetchRealStats();
    }
  }, [profile?.id]);

  const fetchRealStats = async () => {
    try {
      // Fetch real revenue data from campaigns and partnerships
      const [campaignsResponse, partnershipsResponse, prospectsResponse] = await Promise.all([
        supabase.from('advertising_campaigns').select('total_budget, campaign_status'),
        supabase.from('active_partnerships').select('monthly_revenue_generated'),
        supabase.from('advertiser_prospects').select('pipeline_stage, estimated_campaign_value')
      ]);

      // Calculate total revenue from completed campaigns
      const campaignRevenue = campaignsResponse.data
        ?.filter(c => c.campaign_status === 'completed')
        .reduce((sum, c) => sum + (c.total_budget || 0), 0) || 0;

      // Calculate monthly partnership revenue (assume 12 months)
      const partnershipRevenue = partnershipsResponse.data
        ?.reduce((sum, p) => sum + (p.monthly_revenue_generated || 0), 0) * 12 || 0;

      // Calculate total prospects
      const totalProspects = prospectsResponse.data?.length || 0;

      // Calculate success rate (closed deals / total prospects)
      const closedDeals = prospectsResponse.data
        ?.filter(p => p.pipeline_stage === 'closed').length || 0;
      const successRate = totalProspects > 0 ? Math.round((closedDeals / totalProspects) * 100) : 0;

      setStats({
        totalRevenue: campaignRevenue + partnershipRevenue,
        totalProspects,
        toolsUsed: 4, // ROI, CPM, Conversion Calculators + Proposal Templates
        successRate
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculatorTools = [
    {
      title: "ROI Calculator",
      description: "Calculate return on investment for advertising campaigns",
      icon: Calculator,
      comingSoon: false
    },
    {
      title: "CPM Calculator", 
      description: "Calculate cost per thousand impressions for media planning",
      icon: BarChart3,
      comingSoon: false
    },
    {
      title: "Conversion Calculator",
      description: "Track conversion rates and optimize campaign performance",
      icon: Target,
      comingSoon: false
    }
  ];

  const templateTools = [
    {
      title: "Proposal Templates",
      description: "Pre-built proposal templates for different client types",
      icon: FileText,
      comingSoon: false
    },
    {
      title: "Email Templates",
      description: "Sales email templates for outreach and follow-ups",
      icon: Mail,
      comingSoon: true
    },
    {
      title: "Contract Templates",
      description: "Standard contract templates for advertising agreements",
      icon: FileText,
      comingSoon: true
    }
  ];

  const automationTools = [
    {
      title: "Lead Scoring",
      description: "Automatically score and prioritize prospects",
      icon: Users,
      comingSoon: true
    },
    {
      title: "Follow-up Scheduler",
      description: "Automated follow-up scheduling and reminders",
      icon: Calendar,
      comingSoon: true
    },
    {
      title: "Campaign Optimizer",
      description: "AI-powered campaign optimization suggestions",
      icon: Zap,
      comingSoon: true
    }
  ];

  const communicationTools = [
    {
      title: "Video Proposals",
      description: "Create personalized video proposals for prospects",
      icon: Smartphone,
      comingSoon: true
    },
    {
      title: "Client Portal",
      description: "Branded client portal for campaign tracking",
      icon: Globe,
      comingSoon: true
    },
    {
      title: "Chat Integration",
      description: "Integrated chat for real-time client communication",
      icon: MessageSquare,
      comingSoon: true
    }
  ];

  const ToolCard = ({ tool, category, onLaunch }: { tool: any; category: string; onLaunch?: () => void }) => (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <tool.icon className="h-5 w-5 text-primary" />
          </div>
          <div>
            <CardTitle className="text-lg">{tool.title}</CardTitle>
            {tool.comingSoon && (
              <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full">
                Coming Soon
              </span>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <CardDescription className="mb-4">
          {tool.description}
        </CardDescription>
        <Button 
          variant={tool.comingSoon ? "outline" : "default"} 
          className="w-full"
          disabled={tool.comingSoon}
          onClick={onLaunch}
        >
          {tool.comingSoon ? "Coming Soon" : "Launch Tool"}
        </Button>
      </CardContent>
    </Card>
  );

  const formatCurrency = (amount: number) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(1)}K`;
    }
    return `$${amount.toLocaleString()}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Sales Tools</h1>
          <p className="text-muted-foreground mt-2">
            Powerful tools and resources to boost your sales performance
          </p>
        </div>
      </div>

      {/* Real Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <Calculator className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold">{loading ? "..." : stats.toolsUsed}</p>
                <p className="text-sm text-muted-foreground">Available Tools</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <Target className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold">{loading ? "..." : stats.totalProspects}</p>
                <p className="text-sm text-muted-foreground">Total Prospects</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <DollarSign className="h-8 w-8 text-emerald-600" />
              <div>
                <p className="text-2xl font-bold">{loading ? "..." : formatCurrency(stats.totalRevenue)}</p>
                <p className="text-sm text-muted-foreground">Total Revenue</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <Zap className="h-8 w-8 text-purple-600" />
              <div>
                <p className="text-2xl font-bold">{loading ? "..." : `${stats.successRate}%`}</p>
                <p className="text-sm text-muted-foreground">Success Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="calculators" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="calculators">Calculators</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="automation">Automation</TabsTrigger>
          <TabsTrigger value="communication">Communication</TabsTrigger>
        </TabsList>

        <TabsContent value="calculators" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Sales Calculators</CardTitle>
              <CardDescription>
                Calculate ROI, pricing, and performance metrics for your campaigns
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {calculatorTools.map((tool, index) => (
                  <ToolCard 
                    key={index} 
                    tool={tool} 
                    category="calculator"
                    onLaunch={() => {
                      if (tool.title === "ROI Calculator") setActiveModal('roi-calculator');
                      if (tool.title === "CPM Calculator") setActiveModal('cpm-calculator');
                      if (tool.title === "Conversion Calculator") setActiveModal('conversion-calculator');
                    }}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Document Templates</CardTitle>
              <CardDescription>
                Ready-to-use templates for proposals, contracts, and communications
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {templateTools.map((tool, index) => (
                  <ToolCard 
                    key={index} 
                    tool={tool} 
                    category="template"
                    onLaunch={() => tool.title === "Proposal Templates" && setActiveModal('proposal-templates')}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="automation" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Sales Automation</CardTitle>
              <CardDescription>
                Automate repetitive tasks and optimize your sales workflow
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {automationTools.map((tool, index) => (
                  <ToolCard key={index} tool={tool} category="automation" />
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="communication" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Communication Tools</CardTitle>
              <CardDescription>
                Enhance client communication and relationship management
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {communicationTools.map((tool, index) => (
                  <ToolCard key={index} tool={tool} category="communication" />
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Tool Modals */}
      {activeModal === 'roi-calculator' && (
        <ROICalculator 
          open={true}
          onClose={() => setActiveModal(null)}
        />
      )}
      
      {activeModal === 'proposal-templates' && (
        <ProposalTemplates 
          open={true}
          onClose={() => setActiveModal(null)}
        />
      )}
      
      {activeModal === 'cpm-calculator' && (
        <CPMCalculator 
          open={true}
          onClose={() => setActiveModal(null)}
        />
      )}
      
      {activeModal === 'conversion-calculator' && (
        <ConversionCalculator 
          open={true}
          onClose={() => setActiveModal(null)}
        />
      )}
    </div>
  );
};

export default SalesToolsPage;